import React from 'react';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  selectAccessToken,
  selectFrontendParams,
  selectInviteData,
  selectPendingTwoFactor,
  selectUser,
} from '../../store/general/general-slice-selectors';
import { login } from '../../store/general/general-actions';
import { makeStyles } from 'tss-react/mui';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import SphereLogo from '../../media/sphereFullLogo.png';
import SphereLoginBackdrop from '../../media/sphereLoginBackdrop.png';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { CredentialResponse } from '@react-oauth/google';
import CustomLoader from '@common/components/Feedback/CustomLoader';
import LocalLogin from '../admin/LocalLogin';
import { LoginVariant } from '../../models/general/login-details';
import { resetRegisterRestState, resetUserRestStatus } from '../../store/general/general-slice';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import { isHttpError } from '@common/interfaces/api/http-error';
import TwoFactorVerify from '../admin/TwoFactorVerify';
import TwoFactorSetup from '../admin/TwoFactorSetup';
import GoogleLoginInput from 'components/forms/GoogleLoginInput';

const useStyles = makeStyles()((theme) => ({
  sphereLogo: {
    width: `min(${theme.spacing(48)}, 100%)`,
  },
  kemaroLogo: {
    width: theme.spacing(32),
  },
  sphereLoginBackdrop: {
    width: '100vw',
    height: '100vh',
  },
}));

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => selectUser(state));
  const token = useAppSelector((state) => selectAccessToken(state));
  const inviteData = useAppSelector((state) => selectInviteData(state));
  const frontendParams = useAppSelector((state) => selectFrontendParams(state));
  const pendingTwoFactor = useAppSelector((state) => selectPendingTwoFactor(state));

  const googleLoginSuccessHandler = (response: CredentialResponse) => {
    if (response.credential != null) {
      dispatch(login({ token: response.credential, variant: LoginVariant.Google }));
    }
  };

  const googleLoginErrorHandler = () => {
    dispatch(
      resetUserRestStatus({
        postError: { message: t('error-logging-in-with-google'), code: '400', value: null },
      }),
    );
  };

  const closeRegisterSnackbarHandler = () => {
    dispatch(resetRegisterRestState());
  };

  return (
    <React.Fragment>
      <img src={SphereLoginBackdrop} alt="" className={classes.sphereLoginBackdrop} />
      <Dialog maxWidth="sm" open={true}>
        <DialogContent>
          <Stack alignItems="center" spacing={6} maxWidth="sm">
            <img src={SphereLogo} alt="" className={classes.sphereLogo} />

            <Stack spacing={2} width="100%">
              {user.rest.getStatus === HttpFetchStatus.Loading ||
              !token.localStorageChecked ||
              frontendParams.getStatus === HttpFetchStatus.Idle ||
              frontendParams.getStatus === HttpFetchStatus.Loading ||
              user.rest.postStatus === HttpFetchStatus.Loading ? (
                <CustomLoader loadingMessage={t('loading-log-in')} />
              ) : pendingTwoFactor != null &&
                !pendingTwoFactor.setupRequired &&
                pendingTwoFactor.token != null ? (
                <TwoFactorVerify pendingToken={pendingTwoFactor.token} onSuccess={() => {}} />
              ) : pendingTwoFactor != null && pendingTwoFactor.setupRequired ? (
                <TwoFactorSetup
                  pendingToken={pendingTwoFactor.token}
                  variant="page"
                  onComplete={() => {}}
                />
              ) : (
                <React.Fragment>
                  <GoogleLoginInput
                    onSuccess={googleLoginSuccessHandler}
                    onError={googleLoginErrorHandler}
                  />

                  <Divider
                    orientation="horizontal"
                    sx={(theme) => ({ width: '100%', color: theme.palette.action.active })}
                  >
                    {t('info-or')}
                  </Divider>

                  {user.rest.postError != null && isHttpError(user.rest.postError) && (
                    <CustomAlert
                      sx={{ width: '100%', maxWidth: 'sm' }}
                      header={t('error-logging-in')}
                      textMessages={[t(user.rest.postError.message)]}
                      severity="error"
                    />
                  )}

                  <LocalLogin />
                </React.Fragment>
              )}
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      <CustomSnackbar
        open={inviteData.rest.postStatus === HttpFetchStatus.Success}
        title={t('success-registering-member')}
        onClose={closeRegisterSnackbarHandler}
      />
    </React.Fragment>
  );
};

export default LoginPage;
